import { Box } from '@mui/material'
import {
  PlannedProductionViewProps,
  PlannedProductionData,
} from './PlannedProduction.types'
import { useMemo, useRef } from 'react'
import { GridRowId } from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'

const staticColumns = new Set([
  'id',
  'workCentreCode',
  'SourceSiteCode',
  'skuCode',
  'skuName',
  'batchGroupingCode',
  'cycleTime',
  'replenishmentType',
  'SourceWorkCentreCode',
  'minimumProductionOrderQuantity',
  'minimumProductionOrderIncrement',
])

export const PlannedProductionTable = ({
  isLoading,
  data = [],
  weeks,
  onDataSaveInTheTable,
  onWeekEdited,
  uploadFile,
  isUploading,
}: PlannedProductionViewProps) => {
  const apiRef = useRef<GridApiPremium | null>(null)
  const updateTableData = (
    _newData: Record<GridRowId, PlannedProductionData>
  ) => {
    const allRows = apiRef.current
      ? (Array.from(
          apiRef.current.getRowModels().values()
        ) as PlannedProductionData[])
      : []
    onDataSaveInTheTable?.(allRows)
  }

  const weekIndexByDate = useMemo(() => {
    const map: Record<string, number> = {}
    weeks.forEach((week, index) => {
      map[week] = index + 1
    })
    return map
  }, [weeks])
  const columns: ExtendedGridColDef[] = useMemo(() => {
    const staticCols = [
      {
        headerName: 'Work Centre Code',
        field: 'workCentreCode',
        flex: 1,
        minWidth: 150,
        type: 'string',
      },
      {
        headerName: 'Source Site',
        field: 'baseSourceSiteCode',
        flex: 1,
        minWidth: 120,
        type: 'string',
      },
      {
        headerName: 'SKU Code',
        field: 'skuCode',
        flex: 1,
        minWidth: 120,
        type: 'string',
      },
      {
        headerName: 'Batch Group',
        field: 'batchGroupingCode',
        flex: 1,
        minWidth: 120,
        hide: true,
        type: 'string',
      },
      {
        headerName: 'MOQ',
        field: 'minimumProductionOrderQuantity',
        flex: 1,
        minWidth: 100,
        type: 'number',
      },
      {
        headerName: 'MOI',
        field: 'minimumProductionOrderIncrement',
        flex: 1,
        minWidth: 100,
        type: 'number',
      },
    ]

    // Generate week-based columns for production amounts
    const weekCols = weeks.map((week: string) => ({
      field: week,
      headerName: week,
      type: 'number',
      minWidth: 150,
      editable: true,
    }))

    return [...staticCols, ...weekCols]
  }, [data, weeks])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <BaseTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          updateTableData={updateTableData}
          apiRefCallback={(ref) => {
            apiRef.current = ref.current
          }}
          uploadData={uploadFile} // Upload handled by external Toolbar
          isUploading={isUploading}
          isSaving={false}
          checkBoxSelection={true}
          showTableToolBar={true}
          pinnedColumns={['workCentreCode', 'baseSourceSiteCode', 'skuCode']}
          downloadFileName="PlannedProduction"
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: {
              sortModel: [{ field: 'workCentreCode', sort: 'asc' }],
            },
            columns: {
              columnVisibilityModel: {
                batchGroupingCode: false,
              },
            },
          }}
          processRow={(newRow, oldRow) => {
            for (const week of weeks) {
              if (newRow[week] !== oldRow[week]) {
                onWeekEdited?.(Number(week), {
                  workCentreCode: String(newRow.workCentreCode),
                  skuCode: String(newRow.skuCode),
                  weekIndex: weekIndexByDate[week],
                })
              }
            }
            return newRow
          }}
        />
      </Box>
    </Box>
  )
}
